import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REFERENCE_BOOKS_URL, getFetchRequest } from '../../../store/requests';
import { errorCallback, makeUrl } from '../../../util';
import { getPropertyDataById, productSaveService } from '../../../services/productSaveService';

type ReferenceBook = {
    id: string;
    code: string;
    title?: string;
};

type PropertyReferenceBookProps = {
    propertyId: string;
};

let cachedBooks: ReferenceBook[] | null = null;
let booksLoadStarted = false;
const booksWaiters: Array<(books: ReferenceBook[]) => void> = [];

const loadReferenceBooks = (onLoaded: (books: ReferenceBook[]) => void) => {
    if (cachedBooks) {
        onLoaded(cachedBooks);
        return;
    }
    booksWaiters.push(onLoaded);
    if (booksLoadStarted) {
        return;
    }
    booksLoadStarted = true;
    getFetchRequest(
        makeUrl([REFERENCE_BOOKS_URL]),
        (response: unknown) => {
            const list = Array.isArray(response) ? (response as ReferenceBook[]) : [];
            cachedBooks = list.filter((book) => book && book.id);
            const waiters = booksWaiters.splice(0, booksWaiters.length);
            waiters.forEach((waiter) => waiter(cachedBooks || []));
        },
        errorCallback,
    );
};

const PropertyReferenceBook = ({ propertyId }: PropertyReferenceBookProps) => {
    const { i18n } = useTranslation();
    const [books, setBooks] = useState<ReferenceBook[]>(cachedBooks || []);
    const property = getPropertyDataById(propertyId) || {};
    const [selectedId, setSelectedId] = useState(property.referenceBookId || '');

    useEffect(() => {
        let cancelled = false;
        loadReferenceBooks((list) => {
            if (!cancelled) {
                setBooks(list);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedId(getPropertyDataById(propertyId)?.referenceBookId || '');
    }, [propertyId]);

    const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        const book = books.find((item) => item.id === id);
        setSelectedId(id);
        productSaveService.updateProductPropertyReferenceBook(propertyId, id, book?.code || '');
    };

    const selectedMissing = Boolean(
        selectedId && !books.some((book) => book.id === selectedId),
    );

    return (
        <label className="block__text product__form-label">
            <span className="product__form-label_text">
                {i18n.t('app.page.product.referenceBook')}
            </span>
            <select
                className="product__form-input"
                value={selectedId}
                onChange={onChange}
            >
                <option value="">{i18n.t('app.page.product.referenceBookNone')}</option>
                {selectedMissing && (
                    <option value={selectedId}>
                        {property.referenceBookCode || selectedId}
                    </option>
                )}
                {books.map((book) => (
                    <option key={book.id} value={book.id}>
                        {book.title ? `${book.code} — ${book.title}` : book.code}
                    </option>
                ))}
            </select>
        </label>
    );
};

export default PropertyReferenceBook;
